import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Copy, Check, Download, Loader2 } from 'lucide-react';
import { useCallback, useRef } from 'react';
import useTranslation from '@/hooks/useTranslation';
import categoriesColorList from '@/utils/categoriesColorList';
import useCopyState from './useCopyState';
import useDownloadImage from './useDownloadImage';
import {
  BASE_ROW_HEIGHT,
  CHART_MIN_HEIGHT,
  computeYAxisWidth,
  estimateLineCount,
} from './chartHelpers';
import WrappedYAxisTick from './WrappedYAxisTick';

/**
 * TELV1-419 — clustered / grouped bar chart artifact.
 *
 * Renders a 2-D breakdown (one variable BY another) as side-by-side bars:
 * one `<Bar>` per group, sharing the category YAxis. The data is a view of a
 * compute_statistics cross_tabulate / category_breakdown result, validated
 * server-side (every cell value + every category & group label must match
 * tool evidence). Bars are INDEPENDENT counts — categories may overlap, so
 * there is deliberately no stacked/percent-of-whole mode.
 *
 * Layout mirrors the single-series ArtifactChartBlock (horizontal bars,
 * `layout="vertical"`) so long category labels wrap on the YAxis via the
 * shared WrappedYAxisTick.
 */
interface GroupedRow {
  name: string;
  // Group columns are numeric; `name` is the only string key.
  [group: string]: string | number;
}

interface ArtifactGroupedChartBlockProps {
  title: string;
  data: {
    rows: GroupedRow[];
    groups: string[];
    x_label?: string;
    y_label?: string;
  };
}

const DEFAULT_COLORS = categoriesColorList;

/** Minimum readable bar thickness (px) — bars thinner than this are unusable. */
const MIN_BAR_THICKNESS = 14;
/** recharts default barGap between clustered bars within a category band. */
const GROUP_BAR_GAP = 4;
/** Reserve for a one-row legend below the plot so it isn't clipped in the PNG. */
const LEGEND_HEIGHT_BUFFER = 40;
/** Cap per-bar thickness so a 1-group chart doesn't render giant bars. */
const MAX_BAR_SIZE = 80;

/**
 * Per-category row height that accounts for BOTH the number of clustered
 * bars (G) and the wrapped label line count. The bar side needs room for G
 * bars + the gaps between them + the ~20% barCategoryGap margin recharts
 * reserves around the band; the label side reuses ArtifactChartBlock's
 * single-line + half-row-per-extra-line model. We take the larger.
 */
function groupedRowHeight(groupCount: number, labelLineCount: number): number {
  const forBars =
    groupCount * MIN_BAR_THICKNESS +
    Math.max(0, groupCount - 1) * GROUP_BAR_GAP +
    BASE_ROW_HEIGHT * 0.2;
  const forLabel = BASE_ROW_HEIGHT + Math.max(0, labelLineCount - 1) * (BASE_ROW_HEIGHT * 0.5);
  return Math.max(forBars, forLabel);
}

function ArtifactGroupedChartBlock({ title, data }: ArtifactGroupedChartBlockProps) {
  const { lang } = useTranslation();
  const [copied, triggerCopied] = useCopyState();
  const chartCardRef = useRef<HTMLDivElement>(null);
  const [isDownloading, isDone, triggerDownload] = useDownloadImage(chartCardRef, { title });

  const rows = data?.rows ?? [];
  const groups = data?.groups ?? [];

  const handleCopy = useCallback(() => {
    // Wide-format TSV (one column per group) pastes cleanly into Sheets.
    // Read off `data` directly (not the derived consts) so the callback's
    // dependency list stays referentially stable across renders.
    const g = data?.groups ?? [];
    const r = data?.rows ?? [];
    const header = ['', ...g].join('\t');
    const body = r.map((row) => [row.name, ...g.map((grp) => row[grp] ?? 0)].join('\t')).join('\n');
    navigator.clipboard
      .writeText(`${title}\n${header}\n${body}`)
      .then(() => triggerCopied())
      .catch(() => {});
  }, [data, title, triggerCopied]);

  if (!rows.length || !groups.length) return null;

  const yAxisWidth = computeYAxisWidth(rows);

  const chartHeight =
    Math.max(
      CHART_MIN_HEIGHT,
      rows.reduce((total, row) => {
        const lines = estimateLineCount(row.name, yAxisWidth);
        return total + groupedRowHeight(groups.length, lines);
      }, 0),
    ) + LEGEND_HEIGHT_BUFFER;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>
        <div className="flex items-center gap-0.5">
          {/* Download PNG */}
          <button
            type="button"
            onClick={triggerDownload}
            disabled={isDownloading}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            title={lang.get('msg.downloadChartPng')}
            aria-label={lang.get('msg.downloadChartPng')}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {isDownloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : isDone ? (
              <Check className="h-3.5 w-3.5 text-primary" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
          </button>

          {/* Copy data */}
          <button
            type="button"
            onClick={handleCopy}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            title={lang.get('msg.copyArtifact')}
            aria-label={lang.get('msg.copyArtifact')}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-primary" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </div>

      {/* White card — this is what gets captured by html-to-image (legend
          included: recharts portals it into the recharts-wrapper which is a
          descendant of this ref). */}
      <div ref={chartCardRef} className="bg-white border border-neutral-200 rounded-lg p-4">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={rows}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            barGap={GROUP_BAR_GAP}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: '#737373' }}
              // Horizontal (rotated) layout: the XAxis is the VALUE axis, so it
              // carries y_label (e.g. "Number of Respondents") — matching the
              // single-series ArtifactChartBlock. The category dimension
              // (x_label) is conveyed by the YAxis tick labels + chart title.
              label={
                data.y_label
                  ? { value: data.y_label, position: 'insideBottom', offset: -5, fontSize: 11 }
                  : undefined
              }
            />
            <YAxis
              type="category"
              dataKey="name"
              width={yAxisWidth}
              tick={<WrappedYAxisTick width={yAxisWidth} />}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: '1px solid #e5e5e5',
                fontSize: 12,
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
            {/* One bar per group, side-by-side (no stackId). Color is keyed
                by GROUP index so the legend swatch matches the bar fill.
                isAnimationActive={false} (TELV1-419): the grow-from-zero mount
                animation gets stuck at its 0 start-frame when the chart mounts
                inside the artifact panel, so the clustered bars render 0px wide
                until a window resize; the panel doesn't benefit from it and it
                would otherwise taint the PNG export. */}
            {groups.map((group, i) => (
              <Bar
                key={group}
                dataKey={group}
                name={group}
                fill={DEFAULT_COLORS[i % DEFAULT_COLORS.length]}
                radius={[0, 4, 4, 0]}
                maxBarSize={MAX_BAR_SIZE}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default ArtifactGroupedChartBlock;
